import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subscription } from "rxjs";
import { PopoverComponent } from "../../../shared/popover/popover.component";
import { AnnouncementCategory } from "../../../models/announcement/announcement-category";
import { FilterType } from "../../../models/enums";
import { KeyValuePair } from "../../../models/key-value-pair";
import { AnnouncementService } from "../../../services/announcement.service";
import { FilterService } from "../../../services/filter.service";
import { AutoUnsubscribe } from "../../../shared/decorators";
import { Helper } from "../../../shared/helper";

@Component({
  standalone: false,
  selector: "dua-category-filter",
  templateUrl: "./category-filter.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./category-filter.component.scss"],
})
@AutoUnsubscribe()
export class CategoryFilterComponent implements OnInit, OnDestroy {
  @ViewChild("popover") popover!: PopoverComponent;

  filterType = FilterType.Category;
  defaultTitle = "Samtliga kategorier";
  displayValue = this.defaultTitle;
  open = false;
  allChecked = true;

  categories: AnnouncementCategory[] = [];
  checkedStatus: KeyValuePair<string, boolean>[] = [];

  /** Prevents echo-updates from our own filter emits from rewriting local checkbox state. */
  private applyingLocalChange = false;

  categoryChangeSubscription!: Subscription;
  categoriesSubscription!: Subscription;
  currentOpenSubscription!: Subscription;

  constructor(
    private filterService: FilterService,
    private announcementService: AnnouncementService
  ) {}

  ngOnInit(): void {
    this.categoriesSubscription = this.announcementService
      .getAnnouncementCategoriesObservable()
      .subscribe((categories) => {
        this.categories = categories ?? [];
        const saved = this.filterService.getCategoryFilter();
        this.syncFromSelection(
          saved?.length ? saved : this.categories.map((c) => c.categoryType)
        );
      });

    this.categoryChangeSubscription = this.filterService
      .getAnnouncementCategoryChange()
      .subscribe((change) => {
        if (this.applyingLocalChange) {
          return;
        }
        const selectedTypes = change?.length
          ? change.map((c) => c.categoryType)
          : this.categories.map((c) => c.categoryType);
        this.syncFromSelection(selectedTypes);
      });

    this.currentOpenSubscription = this.filterService
      .getCurrentOpenedFilterChanged()
      .subscribe((filterType) => {
        if (filterType != this.filterType) {
          this.closePopover();
        }
      });
  }

  onToggle(payload: { open: boolean; event: Event }) {
    this.popover?.toggle(payload.event);
  }

  onPopoverShow() {
    this.open = true;
    this.filterService.setCurrentFilterType(this.filterType);
  }

  onPopoverHide() {
    this.open = false;
  }

  closePopover() {
    this.popover?.hide();
    this.open = false;
  }

  onCategoryCheckedChange(displayName: string, checked: boolean) {
    this.checkedStatus.forEach((x) => {
      if (x.key === displayName) {
        x.value = checked;
      }
    });
    this.refreshAllChecked();
    this.emitSelection();
  }

  onAllCheckedChange(checked: boolean) {
    this.allChecked = checked;
    this.checkedStatus.forEach((x) => {
      x.value = checked;
    });
    this.emitSelection();
  }

  private syncFromSelection(selectedTypes: string[]) {
    if (!this.categories?.length) {
      return;
    }
    const allTypes = this.categories.map((c) => c.categoryType);
    const treatAsAll =
      !selectedTypes?.length ||
      Helper.compareStringArrays(selectedTypes, allTypes);

    this.checkedStatus = this.categories.map(
      (c) =>
        new KeyValuePair(
          c.displayName,
          treatAsAll || selectedTypes.includes(c.categoryType)
        )
    );
    this.refreshAllChecked();
    this.updateDisplayValue();
  }

  private refreshAllChecked() {
    this.allChecked =
      this.checkedStatus.length > 0 && this.checkedStatus.every((x) => x.value);
  }

  private emitSelection() {
    const selected = this.categories.filter((c) =>
      this.checkedStatus.some((x) => x.key === c.displayName && x.value)
    );

    this.applyingLocalChange = true;
    this.filterService.setAnnouncementCategories(selected);
    this.filterService.setCategoryFilter(selected.map((c) => c.categoryType));
    this.applyingLocalChange = false;

    this.updateDisplayValue();
  }

  private updateDisplayValue() {
    if (this.allChecked || this.checkedStatus.every((x) => !x.value)) {
      // None selected is treated as "all" for filtering; keep label consistent when all checked.
      this.displayValue = this.allChecked
        ? this.defaultTitle
        : "Inga kategorier";
      return;
    }
    const selectedNames = this.checkedStatus
      .filter((x) => x.value)
      .map((x) => x.key);
    this.displayValue =
      selectedNames.length <= 2
        ? selectedNames.join(", ")
        : `${selectedNames.length} kategorier`;
  }

  ngOnDestroy() {}
}
